import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBars, 
  faSearch,
  faBell,
  faUserCircle, 
  faSignOutAlt, 
  faUser, 
  faCog 
} from '@fortawesome/free-solid-svg-icons';
import { getPhotoUrl } from '../utils/imageUtils';
import NotificacoesButton from './NotificacoesDropdown';

const Header = ({ onMenuClick }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const dropdownRef = useRef(null);
  
  // Verificar se o usuário pode ver notificações (operadores nível >= 4 OU coordenadores nível 2-3)
  const nivelAcesso = user?.nivel_acesso || user?.nivelAcesso || user?.perfil?.nivelAcesso;
  const podeVerNotificacoes = nivelAcesso >= 2;

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDropdownClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Erro no logout:', error);
    }
  };

  const avatarSrc = getPhotoUrl(user?.foto);
  const avatarExists = Boolean(avatarSrc && avatarSrc !== '' && avatarSrc !== undefined);

  return (
    <header className="bg-gradient-to-r from-green-400 to-blue-500 text-white shadow-md p-4 z-30 relative">
      <div className="flex justify-between items-center pl-0 sm:pl-1 lg:pl-2 pr-2 sm:pr-4 lg:pr-6">
        <div className="flex items-center space-x-2">
          <button
            className="menu-hamburger-btn block lg:hidden p-2 rounded bg-white text-gray-600 hover:bg-gray-200 mr-2 z-30"
            onClick={onMenuClick}
          >
            {faBars && <FontAwesomeIcon icon={faBars} />}
          </button>
          <div className="flex items-center space-x-2">
            <span className="text-lg sm:text-xl font-bold">Instituto Bíblico Ebenezer</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 sm:space-x-4">
          <button className="hidden sm:block p-1 sm:p-2 rounded-full bg-white text-gray-600 hover:bg-gray-200 transition duration-200">
            <FontAwesomeIcon icon={faSearch} />
          </button>
          
          {/* Notificações - Visível para operadores (nível >= 4) e coordenadores (nível 2-3) */}
          <div className="hidden sm:block">
            <NotificacoesButton nivelAcesso={nivelAcesso} />
          </div>
          
          {/* User Dropdown */}
          <div className="relative flex items-center">
            {avatarExists ? (
              <img 
                src={avatarSrc} 
                alt="User Avatar" 
                className="h-8 w-8 sm:h-8 sm:w-8 rounded-full cursor-pointer transition duration-200 hover:ring-2 hover:ring-white hover:ring-opacity-50" 
                onClick={handleDropdownClick} 
              />
            ) : (
              <span 
                className="h-6 w-6 sm:h-8 sm:w-8 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 cursor-pointer transition duration-200 hover:bg-gray-300" 
                onClick={handleDropdownClick}
              >
                <FontAwesomeIcon icon={faUserCircle} size="lg" />
              </span>
            )}
            
            <div className="hidden sm:flex flex-col ml-2">
              <span className="text-sm font-medium">{user?.nome || 'Usuário'}</span>
              <span className="text-xs opacity-75">{user?.perfil_nome || 'Sem perfil'}</span>
            </div>

            {/* Dropdown Menu */}
            <div
              ref={dropdownRef}
              className={`absolute right-0 top-10 sm:top-12 w-56 bg-white rounded-lg shadow-xl z-40 text-gray-800 transition-all duration-300 border ${
                isDropdownOpen 
                  ? 'opacity-100 scale-100 pointer-events-auto' 
                  : 'opacity-0 scale-95 pointer-events-none'
              }`}
              style={{ transformOrigin: 'top right' }}
            >
              {/* User Info */}
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center">
                  {avatarExists ? (
                    <img src={avatarSrc} alt="Avatar" className="h-10 w-10 rounded-full" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <FontAwesomeIcon icon={faUserCircle} className="text-gray-500" />
                    </div>
                  )}
                  <div className="ml-3">
                    <p className="font-medium text-sm">{user?.nome || 'Usuário'}</p>
                    <p className="text-xs text-gray-500">{user?.email || ''}</p>
                    <p className="text-xs text-blue-600">{user?.perfil_nome || ''}</p>
                  </div>
                </div>
              </div>

              {/* Mobile Search and Notifications */}
              <div className="block sm:hidden border-b border-gray-200">
                <button className="w-full flex items-center px-4 py-2 hover:bg-gray-100 transition duration-200">
                  <FontAwesomeIcon icon={faSearch} className="mr-3 text-gray-400" />
                  <span className="text-sm">Buscar</span>
                </button>
                {podeVerNotificacoes && (
                  <Link 
                    to="/notificacoes" 
                    className="w-full flex items-center px-4 py-2 hover:bg-gray-100 transition duration-200"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <FontAwesomeIcon icon={faBell} className="mr-3 text-gray-400" />
                    <span className="text-sm">Notificações</span>
                  </Link>
                )}
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <Link 
                  to="/meu-perfil" 
                  className="w-full flex items-center px-4 py-2 hover:bg-gray-100 transition duration-200"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  <FontAwesomeIcon icon={faUser} className="mr-3 text-gray-400" />
                  <span className="text-sm">Meu Perfil</span>
                </Link>
                <button className="w-full flex items-center px-4 py-2 hover:bg-gray-100 transition duration-200">
                  <FontAwesomeIcon icon={faCog} className="mr-3 text-gray-400" />
                  <span className="text-sm">Configurações</span>
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-gray-200">
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2 hover:bg-red-50 transition duration-200 text-red-600"
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="mr-3" />
                  <span className="text-sm">Sair</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
